import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';

import { signinArgsForCurrentLocation, useAuth, useLoginRoute } from './cree8-auth';
import { routes } from './app/router';
import { api } from './api';

/**
 * Router host. Mounted only once the user is authenticated.
 *
 * `createBrowserRouter` calls `initialize()` eagerly — it runs the matched
 * route's `loader` (e.g. `/` → GET /me) the moment the router is created. So
 * the router must NOT be created until an access token is available, otherwise
 * that first loader request goes out with no Authorization header and the API
 * rejects it. Creating it here (post auth-gate) guarantees a token; memoizing
 * it means silent token renews re-render the parent but never remount this
 * component, so the router is built exactly once and navigation state survives.
 */
const RoutedApp = () => {
  const router = useMemo(() => createBrowserRouter(routes), []);
  return <RouterProvider router={router} />;
};

/**
 * Auth gate. Shows the loading splash until CREE8 reports an authenticated
 * user, then wires the API client's token getters and renders the router.
 */
export const AuthenticatedApp = () => {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  useLoginRoute();

  // Register the token getters once, reading from a ref so they always
  // return the latest token without re-running (and re-triggering
  // signinRedirect) on every silent renew, which only changes `auth.user`.
  useEffect(() => {
    api.setTokenGetter(() => authRef.current.user?.access_token);
    api.setIdTokenGetter(() => authRef.current.user?.id_token);
  }, []);

  useEffect(() => {
    if (auth.isLoading) return;

    if (!auth.isAuthenticated) {
      // Capture the deep link before redirecting: the callback lands on the
      // registered redirect_uri (the bare origin), so the only way back to
      // e.g. /accept-invite/:id?token=… is through the OAuth `state`.
      void auth.signinRedirect(signinArgsForCurrentLocation());
      return;
    }
  }, [auth.isLoading, auth.isAuthenticated]);

  if (auth.isLoading || !auth.isAuthenticated) {
    return (
      <div id="loading-container" className="loading-container">
        <div className="page-loader">
          <div className="pulse"></div>
        </div>
      </div>
    );
  }

  return <RoutedApp />;
};
